import { Component, ViewChild, OnDestroy, Inject, Optional, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { PhotoCaptureModalData, PhotoCaptureModalResult } from '../../models';
import { CameraPhotoComponent } from '../camera-photo/camera-photo.component';

@Component({
  selector: 'cpf-photo-capture-modal',
  templateUrl: './photo-capture-modal.component.html',
  styleUrls: ['./photo-capture-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PhotoCaptureModalComponent implements OnDestroy {
  @ViewChild(CameraPhotoComponent) cameraComponent!: CameraPhotoComponent;

  isGeneratingPdf = false;
  error: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<PhotoCaptureModalComponent, PhotoCaptureModalResult>,
    private translate: TranslateService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: PhotoCaptureModalData | null
  ) {}

  ngOnDestroy() {
    this.cleanupCamera();
  }

  get maxSizeBytes(): number | null {
    return this.data?.maxSizeBytes ?? null;
  }

  get hasPhotos(): boolean {
    return this.cameraComponent?.capturedPhotos?.length > 0;
  }

  get isOverSizeBudget(): boolean {
    return this.cameraComponent?.isOverSizeBudget ?? false;
  }

  close() {
    this.cleanupCamera();
    this.dialogRef.close();
  }

  cancel() {
    this.cleanupCamera();
    this.dialogRef.close();
  }

  private cleanupCamera() {
    if (this.cameraComponent) {
      this.cameraComponent.stopCamera();
    }
  }

  async confirm() {
    // Generating is not instant for a long photo series, and the modal stays open while it
    // runs - without this guard a second tap would build and close the PDF twice.
    if (!this.hasPhotos || this.isGeneratingPdf || this.isOverSizeBudget) {
      return;
    }

    this.isGeneratingPdf = true;
    this.error = null;

    try {
      const pdfBlob = await this.cameraComponent.generatePDFBlob();
      this.cleanupCamera();

      this.dialogRef.close({ pdfBlob });
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Keep the modal open with the photos intact so the user can retry instead of being
      // left with a button that looks broken.
      this.error = error instanceof Error && error.message
        ? error.message
        : this.translate.instant('camera-photo.alerts.pdf-generation-error');
    } finally {
      this.isGeneratingPdf = false;
    }
  }
}
